import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Colors } from '@/constants/Colors';
import {
  emptyMatchReactionCounts,
  getExclusiveMatchReaction,
  getMatchReactionToolbarItems,
  isMatchReactionAvailable,
} from '@/constants/matchReactions';
import { useTheme } from '@/hooks/useTheme';
import { apiService } from '@/services/api';
import type { Match, MatchReactionType, MatchReactionsSummary } from '@/types';

interface MatchReactionBarProps {
  match: Match;
  matchKey: string;
  tifosiCle?: string;
  /** `toolbar` : barre d’actions sous la carte match (style réseaux sociaux). */
  variant?: 'card' | 'toolbar';
}

export function MatchReactionBar({ match, matchKey, tifosiCle, variant = 'card' }: MatchReactionBarProps) {
  const colors = useTheme();
  const navigate = useNavigate();
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [summary, setSummary] = useState<MatchReactionsSummary>({
    counts: emptyMatchReactionCounts(),
    mine: [],
  });
  const [toggling, setToggling] = useState<MatchReactionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phaseHint, setPhaseHint] = useState<string | null>(null);

  const configured = apiService.isMatchReactionsConfigured();
  const toolbarItems = useMemo(
    () => getMatchReactionToolbarItems(match, new Date(nowTick)),
    [match, nowTick],
  );
  const mineSet = useMemo(() => new Set(summary.mine), [summary.mine]);

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const loadReactions = useCallback(async () => {
    if (!configured || !matchKey) return;
    setError(null);
    try {
      const res = await apiService.getMatchReactionsByMatch(matchKey, tifosiCle);
      setSummary(res.data);
    } catch {
      setError('Impossible de charger les réactions.');
    }
  }, [configured, matchKey, tifosiCle]);

  useEffect(() => {
    void loadReactions();
  }, [loadReactions]);

  const persistReaction = async (reaction: MatchReactionType, active: boolean) => {
    if (!tifosiCle) return;
    await apiService.setMatchReaction({ tifosiCle, matchKey, reaction, active });
  };

  const handleToggle = async (reaction: MatchReactionType, enabled: boolean, hint?: string) => {
    if (!tifosiCle) {
      navigate('/login');
      return;
    }
    if (!enabled) {
      setPhaseHint(hint ?? 'Réaction indisponible pour le moment.');
      window.setTimeout(() => setPhaseHint(null), 2800);
      return;
    }
    if (!isMatchReactionAvailable(match, reaction, new Date())) return;
    if (toggling) return;

    const wasActive = mineSet.has(reaction);
    const exclusive = !wasActive ? getExclusiveMatchReaction(reaction) : null;
    const deactivateExclusive = exclusive && mineSet.has(exclusive);

    setToggling(reaction);
    setError(null);
    setPhaseHint(null);
    setSummary((prev) => {
      const counts = { ...prev.counts };
      const mine = new Set(prev.mine);
      if (wasActive) {
        counts[reaction] = Math.max(0, counts[reaction] - 1);
        mine.delete(reaction);
      } else {
        counts[reaction] += 1;
        mine.add(reaction);
        if (deactivateExclusive && exclusive) {
          counts[exclusive] = Math.max(0, counts[exclusive] - 1);
          mine.delete(exclusive);
        }
      }
      return { counts, mine: [...mine] };
    });

    try {
      if (deactivateExclusive && exclusive) {
        await persistReaction(exclusive, false);
      }
      await persistReaction(reaction, !wasActive);
      await loadReactions();
    } catch {
      setError('Échec de l’enregistrement. Réessaie.');
      await loadReactions();
    } finally {
      setToggling(null);
    }
  };

  if (!configured) return null;

  const toolbar = variant === 'toolbar';
  const items = toolbar ? toolbarItems : toolbarItems.filter((item) => item.enabled);

  return (
    <div
      style={
        toolbar
          ? {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '12px 14px',
              borderTop: `1px solid ${Colors.primary}20`,
              backgroundColor: colors.surface,
            }
          : {
              marginTop: 8,
              marginBottom: 8,
              padding: 14,
              borderRadius: 16,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surface,
            }
      }
    >
      {!toolbar ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>Réactions</span>
        </div>
      ) : null}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: toolbar ? 'center' : 'flex-start',
          width: toolbar ? '100%' : undefined,
          gap: toolbar ? 18 : 8,
        }}
      >
        {items.map((def) => {
          const active = mineSet.has(def.id);
          const count = summary.counts[def.id] ?? 0;
          const busy = toggling === def.id;
          const faded = toolbar && (!def.enabled || !active);
          return (
            <button
              key={def.id}
              type="button"
              onClick={() => void handleToggle(def.id, def.enabled, def.phaseHint)}
              disabled={busy}
              aria-pressed={active}
              aria-disabled={!def.enabled}
              aria-label={`${def.label}${count > 0 ? `, ${count}` : ''}`}
              title={def.enabled ? def.label : def.phaseHint ?? def.label}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                minHeight: toolbar ? 32 : 40,
                padding: toolbar ? '4px 4px' : '8px 12px',
                borderRadius: toolbar ? 6 : 999,
                border: toolbar ? 'none' : `1px solid ${active ? Colors.primary : colors.border}`,
                background: 'transparent',
                cursor: busy ? 'wait' : def.enabled ? 'pointer' : 'default',
                opacity: busy ? 0.5 : 1,
              }}
            >
              <span
                style={{
                  fontSize: toolbar ? 22 : 18,
                  lineHeight: 1,
                  opacity: faded ? 0.35 : 1,
                  filter: faded ? 'grayscale(1)' : 'none',
                  transition: 'opacity 0.15s ease, filter 0.15s ease',
                }}
                aria-hidden
              >
                {def.emoji}
              </span>
              {count > 0 ? (
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: active ? colors.text : colors.textSecondary,
                    opacity: faded ? 0.45 : 1,
                    minWidth: 10,
                  }}
                >
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {phaseHint ? (
        <p style={{ margin: '8px 0 0', fontSize: 11, textAlign: 'center', color: colors.textSecondary }}>
          {phaseHint}
        </p>
      ) : null}
      {!toolbar && !tifosiCle ? (
        <p style={{ margin: '10px 0 0', fontSize: 12, color: colors.textSecondary }}>
          Connecte-toi pour réagir au match.
        </p>
      ) : null}
      {error ? (
        <p style={{ margin: '8px 0 0', fontSize: 11, textAlign: 'center', color: '#ff6b6b', flexShrink: 0 }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
